import React, { useState, useEffect } from 'react';
import { getApiUrl } from '../config/apiConfig';
import { useAuth } from '../contexts/AuthContext';
import { useCaja } from '../contexts/CajaContext';
import BarrasAdminComponent from './BarrasAdminComponent';

// ─── Estilos reutilizables (light theme) ──────────────────────────────────────
const inputStyle = {
  padding: '0.45rem 0.75rem',
  borderRadius: 6,
  border: '1.5px solid #e2e8f0',
  background: '#fff',
  color: '#2d3748',
  fontSize: '0.9rem',
  width: '100%',
  boxSizing: 'border-box',
  fontFamily: 'inherit',
};

const ROLES_VALIDOS = ['ENCARGADO', 'CAJERO', 'MOZO', 'COCINERO', 'BARTENDER'];

const ROL_BADGE = {
  ENCARGADO:  { bg: '#dbeafe', color: '#1d4ed8' },
  CAJERO:     { bg: '#dcfce7', color: '#15803d' },
  MOZO:       { bg: '#fef9c3', color: '#a16207' },
  COCINERO:   { bg: '#f3e8ff', color: '#7e22ce' },
  BARTENDER:  { bg: '#ccfbf1', color: '#0f766e' },
};

// ─── Mensajes de feedback ──────────────────────────────────────────────────────
const MsgError = ({ msg }) => msg ? (
  <div style={{ background: '#fff1f2', borderLeft: '4px solid #f43f5e', color: '#9f1239', padding: '0.6rem 1rem', borderRadius: 6, marginBottom: '1rem', fontSize: '0.9rem' }}>{msg}</div>
) : null;

const MsgExito = ({ msg }) => msg ? (
  <div style={{ background: '#f0fdf4', borderLeft: '4px solid #38a169', color: '#276749', padding: '0.6rem 1rem', borderRadius: 6, marginBottom: '1rem', fontSize: '0.9rem' }}>{msg}</div>
) : null;

// ─── Diálogo de confirmación estilizado ──────────────────────────────────────
const ConfirmDialog = ({ title, message, onConfirm, onCancel, confirmText = "Confirmar", cancelText = "Cancelar", loading = false }) => (
  <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
    <div style={{ background: '#fff', borderRadius: 12, padding: '1.75rem 2rem', width: '100%', maxWidth: 400, boxShadow: '0 20px 60px rgba(0,0,0,0.18)' }}>
      <h3 style={{ margin: '0 0 1rem', fontSize: '1.1rem', fontWeight: 700, color: '#1e293b' }}>{title}</h3>
      <p style={{ margin: '0 0 1.5rem', color: '#64748b', fontSize: '0.95rem', lineHeight: '1.5' }}>{message}</p>
      <div style={{ display: 'flex', gap: '0.6rem', justifyContent: 'flex-end' }}>
        <button onClick={onCancel} style={{ padding: '0.45rem 1.1rem', borderRadius: 6, border: '1px solid #e2e8f0', background: '#fff', color: '#64748b', fontWeight: 600, cursor: 'pointer' }}>{cancelText}</button>
        <button onClick={onConfirm} disabled={loading} style={{ padding: '0.45rem 1.3rem', borderRadius: 6, border: 'none', background: '#f43f5e', color: '#fff', fontWeight: 600, cursor: loading ? 'wait' : 'pointer' }}>{loading ? '...' : confirmText}</button>
      </div>
    </div>
  </div>
);

// ─── Sub-componente: Tab Cajeros ───────────────────────────────────────────────
const TabCajeros = () => {
  const { auth } = useAuth();
  const { cajasAbiertas } = useCaja();

  const [cajeros, setCajeros] = useState([]);
  const [loadingCajeros, setLoadingCajeros] = useState(true);
  const [guardandoAsig, setGuardandoAsig] = useState({});
  const [pisosEdit, setPisosEdit] = useState({});
  const [guardandoPiso, setGuardandoPiso] = useState({});
  const [pisosDisponiblesCajero, setPisosDisponiblesCajero] = useState([]);
  const [barrasEdit, setBarrasEdit] = useState({});
  const [guardandoBarra, setGuardandoBarra] = useState({});
  const [barrasDisponibles, setBarrasDisponibles] = useState([]);

  const [nuevoNombre, setNuevoNombre] = useState('');
  const [nuevoApellido, setNuevoApellido] = useState('');
  const [nuevoUsuario, setNuevoUsuario] = useState('');
  const [nuevoPassword, setNuevoPassword] = useState('');
  const [creandoCajero, setCreandoCajero] = useState(false);

  const [error, setError] = useState('');
  const [exito, setExito] = useState('');

  const headers = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${auth?.token}` };
  const mostrarExito = (m) => { setExito(m); setError(''); setTimeout(() => setExito(''), 3000); };
  const mostrarError = (m) => { setError(m); setExito(''); };

  const fetchCajeros = async () => {
    setLoadingCajeros(true);
    try {
      const res = await fetch(getApiUrl('/caja/cajeros'), { headers });
      if (!res.ok) throw new Error('Error al cargar cajeros');
      const data = await res.json();
      setCajeros(data);
      // Inicializar estado de edición de piso y barra por cajero
      const pisoMap = {};
      const barraMap = {};
      data.forEach(c => {
        pisoMap[c.id] = c.piso_asignado !== null && c.piso_asignado !== undefined ? String(c.piso_asignado) : '';
        barraMap[c.id] = c.id_barra_asignada ? String(c.id_barra_asignada) : '';
      });
      setPisosEdit(pisoMap);
      setBarrasEdit(barraMap);
    } catch (e) { mostrarError(e.message); }
    finally { setLoadingCajeros(false); }
  };

  useEffect(() => {
    fetchCajeros();
    // Cargar pisos disponibles desde mesas
    fetch(getApiUrl('/mesas'), { headers })
      .then(r => r.ok ? r.json() : [])
      .then(data => {
        const pisos = [...new Set(data.map(m => Number(m.piso)).filter(p => p > 0))].sort((a, b) => a - b);
        setPisosDisponiblesCajero(pisos);
      })
      .catch(() => {});
    // Cargar barras disponibles
    fetch(getApiUrl('/barras'), { headers })
      .then(r => r.ok ? r.json() : [])
      .then(data => setBarrasDisponibles(data))
      .catch(() => {});
  }, []);

  const handleCrearCajero = async (e) => {
    e.preventDefault();
    setCreandoCajero(true);
    try {
      const res = await fetch(getApiUrl('/caja/cajeros/nuevo'), {
        method: 'POST', headers,
        body: JSON.stringify({ nombre: nuevoNombre, apellido: nuevoApellido, usuario: nuevoUsuario, contraseña: nuevoPassword }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Error al crear cajero');
      setCajeros(prev => [...prev, { ...data, id_caja_asignada: null, nombre_caja: null }]);
      setNuevoNombre(''); setNuevoApellido(''); setNuevoUsuario(''); setNuevoPassword('');
      mostrarExito(`Cajero "${data.nombre}" creado correctamente.`);
    } catch (e) { mostrarError(e.message); }
    finally { setCreandoCajero(false); }
  };

  const handleAsignarCaja = async (idUsuario, idCaja) => {
    setGuardandoAsig(prev => ({ ...prev, [idUsuario]: true }));
    try {
      const res = await fetch(getApiUrl(`/caja/cajeros/${idUsuario}`), {
        method: 'PUT', headers,
        body: JSON.stringify({ id_caja: idCaja || null }),
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.message || 'Error'); }
      const cajaSeleccionada = cajasAbiertas.find(c => c.id === idCaja);
      setCajeros(prev => prev.map(c => c.id !== idUsuario ? c : {
        ...c, id_caja_asignada: idCaja || null, nombre_caja: cajaSeleccionada?.nombre || null,
      }));
      mostrarExito('Asignación guardada.');
    } catch (e) { mostrarError(e.message); }
    finally { setGuardandoAsig(prev => ({ ...prev, [idUsuario]: false })); }
  };

  const handleAsignarPisoCajero = async (idUsuario, piso) => {
    setPisosEdit(prev => ({ ...prev, [idUsuario]: piso }));
    setGuardandoPiso(prev => ({ ...prev, [idUsuario]: true }));
    try {
      const res = await fetch(getApiUrl(`/usuarios-admin/${idUsuario}`), {
        method: 'PUT', headers,
        body: JSON.stringify({ piso_asignado: piso !== '' ? parseInt(piso) : -1 }),
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.message || 'Error'); }
      setCajeros(prev => prev.map(c => c.id !== idUsuario ? c : {
        ...c, piso_asignado: piso !== '' ? parseInt(piso) : null,
      }));
      mostrarExito('Piso guardado.');
    } catch (e) { mostrarError(e.message); }
    finally { setGuardandoPiso(prev => ({ ...prev, [idUsuario]: false })); }
  };

  const handleAsignarBarraCajero = async (idUsuario, idBarra) => {
    setBarrasEdit(prev => ({ ...prev, [idUsuario]: idBarra }));
    setGuardandoBarra(prev => ({ ...prev, [idUsuario]: true }));
    try {
      const res = await fetch(getApiUrl(`/usuarios-admin/${idUsuario}`), {
        method: 'PUT', headers,
        body: JSON.stringify({ id_barra: idBarra ? parseInt(idBarra) : null }),
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.message || 'Error'); }
      const barraObj = barrasDisponibles.find(b => b.id === parseInt(idBarra));
      setCajeros(prev => prev.map(c => c.id !== idUsuario ? c : {
        ...c, id_barra_asignada: idBarra ? parseInt(idBarra) : null,
        nombre_barra: barraObj?.nombre || null,
      }));
      mostrarExito('Barra guardada.');
    } catch (e) { mostrarError(e.message); }
    finally { setGuardandoBarra(prev => ({ ...prev, [idUsuario]: false })); }
  };

  const formDisabled = creandoCajero || !nuevoNombre.trim() || !nuevoUsuario.trim() || !nuevoPassword.trim();

  return (
    <div>
      {/* Formulario nuevo cajero */}
      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '1.25rem 1.5rem', marginBottom: '1.5rem', boxShadow: '0 4px 15px rgba(0,0,0,0.07)' }}>
        <h3 style={{ color: '#1e293b', fontSize: '1rem', fontWeight: 700, marginBottom: '1rem', marginTop: 0, borderLeft: '3px solid #f05b41', paddingLeft: '0.75rem' }}>
          Crear nuevo cajero
        </h3>
        <form onSubmit={handleCrearCajero}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '0.6rem', marginBottom: '1rem' }}>
            <input type="text" placeholder="Nombre *" value={nuevoNombre} onChange={e => setNuevoNombre(e.target.value)} required style={inputStyle} />
            <input type="text" placeholder="Apellido" value={nuevoApellido} onChange={e => setNuevoApellido(e.target.value)} style={inputStyle} />
            <input type="text" placeholder="Usuario *" value={nuevoUsuario} onChange={e => setNuevoUsuario(e.target.value)} required style={inputStyle} />
            <input type="password" placeholder="Contraseña *" value={nuevoPassword} onChange={e => setNuevoPassword(e.target.value)} required style={inputStyle} />
          </div>
          <button
            type="submit"
            disabled={formDisabled}
            style={{
              padding: '0.5rem 1.5rem', borderRadius: 8, border: 'none',
              background: formDisabled ? '#e2e8f0' : 'linear-gradient(to right, #f05b41, #f78361)',
              color: formDisabled ? '#94a3b8' : '#fff',
              fontWeight: 600, cursor: formDisabled ? 'not-allowed' : 'pointer',
              fontSize: '0.9rem', transition: 'all 0.2s',
              boxShadow: formDisabled ? 'none' : '0 2px 8px rgba(240,91,65,0.25)',
            }}
          >
            {creandoCajero ? 'Creando...' : '+ Nuevo Cajero'}
          </button>
        </form>
      </div>

      <MsgError msg={error} />
      <MsgExito msg={exito} />

      {cajasAbiertas.length === 0 && (
        <div style={{ background: '#eff6ff', borderLeft: '4px solid #3498db', color: '#1d4ed8', padding: '0.6rem 1rem', borderRadius: 6, marginBottom: '1rem', fontSize: '0.88rem' }}>
          No hay cajas abiertas. Abre una caja antes de asignar cajeros.
        </div>
      )}

      {loadingCajeros ? (
        <p style={{ color: '#64748b' }}>Cargando cajeros...</p>
      ) : cajeros.length === 0 ? (
        <p style={{ color: '#64748b' }}>No hay usuarios con rol CAJERO registrados.</p>
      ) : (
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, overflowX: 'auto', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
          <table style={{ minWidth: '700px', width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                <th style={{ textAlign: 'left', padding: '0.6rem 1rem', color: '#64748b', fontSize: '0.8rem', textTransform: 'uppercase', fontWeight: 600 }}>Cajero</th>
                <th style={{ textAlign: 'left', padding: '0.6rem 1rem', color: '#64748b', fontSize: '0.8rem', textTransform: 'uppercase', fontWeight: 600 }}>Caja asignada</th>
                <th style={{ textAlign: 'left', padding: '0.6rem 1rem', color: '#64748b', fontSize: '0.8rem', textTransform: 'uppercase', fontWeight: 600 }}>Piso</th>
                <th style={{ textAlign: 'left', padding: '0.6rem 1rem', color: '#64748b', fontSize: '0.8rem', textTransform: 'uppercase', fontWeight: 600 }}>Barra / Cocina</th>
              </tr>
            </thead>
            <tbody>
              {cajeros.map(cajero => (
                <tr key={cajero.id} style={{ borderBottom: '1px solid #e2e8f0' }}>
                  <td style={{ padding: '0.75rem 1rem' }}>
                    <div style={{ color: '#1e293b', fontWeight: 500 }}>{cajero.nombre} {cajero.apellido}</div>
                    <div style={{ color: '#94a3b8', fontSize: '0.8rem' }}>@{cajero.usuario}</div>
                  </td>
                  <td style={{ padding: '0.75rem 1rem' }}>
                    <select
                      value={cajero.id_caja_asignada || ''}
                      onChange={e => handleAsignarCaja(cajero.id, e.target.value ? parseInt(e.target.value) : null)}
                      disabled={guardandoAsig[cajero.id]}
                      style={{ ...inputStyle, width: 'auto', minWidth: '180px' }}
                    >
                      <option value="">— Sin asignar —</option>
                      {cajasAbiertas.map(caja => (
                        <option key={caja.id} value={caja.id}>{caja.nombre}</option>
                      ))}
                    </select>
                    {guardandoAsig[cajero.id] && (
                      <span style={{ marginLeft: '0.5rem', color: '#94a3b8', fontSize: '0.8rem' }}>Guardando...</span>
                    )}
                  </td>
                  <td style={{ padding: '0.75rem 1rem' }}>
                    <select
                      value={pisosEdit[cajero.id] ?? ''}
                      onChange={e => handleAsignarPisoCajero(cajero.id, e.target.value)}
                      disabled={guardandoPiso[cajero.id]}
                      style={{ ...inputStyle, width: 'auto', minWidth: '140px' }}
                    >
                      <option value="">Sin restricción</option>
                      {pisosDisponiblesCajero.map(p => (
                        <option key={p} value={p}>Piso {p}</option>
                      ))}
                    </select>
                    {guardandoPiso[cajero.id] && (
                      <span style={{ marginLeft: '0.5rem', color: '#94a3b8', fontSize: '0.8rem' }}>Guardando...</span>
                    )}
                  </td>
                  <td style={{ padding: '0.75rem 1rem' }}>
                    <select
                      value={barrasEdit[cajero.id] ?? ''}
                      onChange={e => handleAsignarBarraCajero(cajero.id, e.target.value)}
                      disabled={guardandoBarra[cajero.id]}
                      style={{ ...inputStyle, width: 'auto', minWidth: '160px' }}
                    >
                      <option value="">Sin restricción</option>
                      {barrasDisponibles.map(b => (
                        <option key={b.id} value={b.id}>{b.nombre}</option>
                      ))}
                    </select>
                    {guardandoBarra[cajero.id] && (
                      <span style={{ marginLeft: '0.5rem', color: '#94a3b8', fontSize: '0.8rem' }}>Guardando...</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ─── Sub-componente: Tab Gestión de Usuarios ───────────────────────────────────
const TabGestionUsuarios = () => {
  const { auth } = useAuth();
  const { cajasAbiertas } = useCaja();
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [barras, setBarras] = useState([]);
  const [pisosDisponibles, setPisosDisponibles] = useState([]);

  // ─ Formulario crear usuario
  const [nuevoNombre, setNuevoNombre] = useState('');
  const [nuevoApellido, setNuevoApellido] = useState('');
  const [nuevoUsuario, setNuevoUsuario] = useState('');
  const [nuevoPassword, setNuevoPassword] = useState('');
  const [nuevoRol, setNuevoRol] = useState('MOZO');
  const [nuevoIdBarra, setNuevoIdBarra] = useState('');
  const [nuevoIdCaja, setNuevoIdCaja] = useState('');
  const [nuevoPiso, setNuevoPiso] = useState('');
  const [creando, setCreando] = useState(false);

  // ─ Edición inline de asignaciones MOZO
  const [editandoMozo, setEditandoMozo] = useState(null); // id del MOZO en edición
  const [editBarra, setEditBarra] = useState('');
  const [editCaja, setEditCaja] = useState('');
  const [editPiso, setEditPiso] = useState('');
  const [guardandoEdit, setGuardandoEdit] = useState({});

  const [toggling, setToggling] = useState({});
  const [error, setError] = useState('');
  const [exito, setExito] = useState('');

  // ─ Modal edición de usuario
  const [modalEdicion, setModalEdicion] = useState(null); // { id, nombre, apellido, usuario }
  const [editNombre, setEditNombre] = useState('');
  const [editApellido, setEditApellido] = useState('');
  const [editUsuario, setEditUsuario] = useState('');
  const [editPassword, setEditPassword] = useState('');
  const [guardandoEdicionU, setGuardandoEdicionU] = useState(false);

  // ─ Confirmación eliminación
  const [usuarioAEliminar, setUsuarioAEliminar] = useState(null); // u
  const [eliminando, setEliminando] = useState({});

  const headers = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${auth?.token}` };
  const mostrarExito = (m) => { setExito(m); setError(''); setTimeout(() => setExito(''), 3000); };
  const mostrarError = (m) => { setError(m); setExito(''); };

  const fetchUsuarios = async () => {
    setLoading(true);
    try {
      const res = await fetch(getApiUrl('/usuarios-admin'), { headers });
      if (!res.ok) throw new Error('Error al cargar usuarios');
      setUsuarios(await res.json());
    } catch (e) { mostrarError(e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    fetchUsuarios();
    fetch(getApiUrl('/barras'), { headers }).then(r => r.ok ? r.json() : []).then(setBarras).catch(() => {});
    fetch(getApiUrl('/mesas'), { headers })
      .then(r => r.ok ? r.json() : [])
      .then(mesas => {
        const pisos = [...new Set(mesas.map(m => m.piso).filter(p => p !== null && p !== undefined))].sort();
        setPisosDisponibles(pisos);
      })
      .catch(() => {});
  }, []);

  const handleCrear = async (e) => {
    e.preventDefault();
    setCreando(true);
    try {
      const body = {
        nombre: nuevoNombre, apellido: nuevoApellido,
        usuario: nuevoUsuario, contraseña: nuevoPassword, rol: nuevoRol,
      };
      if (['COCINERO', 'BARTENDER', 'MOZO'].includes(nuevoRol) && nuevoIdBarra) {
        body.id_barra = parseInt(nuevoIdBarra);
      }
      if (nuevoRol === 'MOZO') {
        if (nuevoIdCaja) body.id_caja = parseInt(nuevoIdCaja);
        if (nuevoPiso !== '') body.piso_asignado = parseInt(nuevoPiso);
      }
      const res = await fetch(getApiUrl('/usuarios-admin'), { method: 'POST', headers, body: JSON.stringify(body) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Error al crear usuario');
      setUsuarios(prev => [...prev, { ...data, estado: true }]);
      setNuevoNombre(''); setNuevoApellido(''); setNuevoUsuario('');
      setNuevoPassword(''); setNuevoRol('MOZO'); setNuevoIdBarra('');
      setNuevoIdCaja(''); setNuevoPiso('');
      mostrarExito(`Usuario "${data.nombre}" creado correctamente.`);
    } catch (e) { mostrarError(e.message); }
    finally { setCreando(false); }
  };

  const handleToggleEstado = async (id, estadoActual) => {
    setToggling(prev => ({ ...prev, [id]: true }));
    try {
      const res = await fetch(getApiUrl(`/usuarios-admin/${id}`), {
        method: 'PUT', headers, body: JSON.stringify({ estado: !estadoActual }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Error al actualizar');
      setUsuarios(prev => prev.map(u => u.id === id ? { ...u, estado: data.estado } : u));
      mostrarExito(`Usuario ${data.estado ? 'activado' : 'desactivado'}.`);
    } catch (e) { mostrarError(e.message); }
    finally { setToggling(prev => ({ ...prev, [id]: false })); }
  };

  const abrirEdicionUsuario = (u) => {
    setModalEdicion(u);
    setEditNombre(u.nombre || '');
    setEditApellido(u.apellido || '');
    setEditUsuario(u.usuario || '');
    setEditPassword('');
  };

  const handleEditarUsuario = async () => {
    if (!editNombre.trim() || !editUsuario.trim()) return mostrarError('Nombre y usuario son obligatorios.');
    setGuardandoEdicionU(true);
    try {
      const body = { nombre: editNombre, apellido: editApellido, usuario: editUsuario };
      if (editPassword.trim()) body.contraseña = editPassword;
      const res = await fetch(getApiUrl(`/usuarios-admin/${modalEdicion.id}/editar`), {
        method: 'PATCH', headers, body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Error al editar');
      setUsuarios(prev => prev.map(u => u.id === modalEdicion.id ? { ...u, nombre: data.nombre, apellido: data.apellido, usuario: data.usuario } : u));
      setModalEdicion(null);
      mostrarExito(`Usuario "${data.nombre}" actualizado correctamente.`);
    } catch (e) { mostrarError(e.message); }
    finally { setGuardandoEdicionU(false); }
  };

  const confirmarEliminar = (u) => setUsuarioAEliminar(u);

  const handleEliminarUsuario = async () => {
    const u = usuarioAEliminar;
    setEliminando(prev => ({ ...prev, [u.id]: true }));
    try {
      const res = await fetch(getApiUrl(`/usuarios-admin/${u.id}/eliminar`), { method: 'DELETE', headers });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Error al eliminar');
      setUsuarios(prev => prev.filter(x => x.id !== u.id));
      setUsuarioAEliminar(null);
      mostrarExito(data.message);
    } catch (e) { mostrarError(e.message); }
    finally { setEliminando(prev => ({ ...prev, [u.id]: false })); }
  };

  const abrirEdicionMozo = (u) => {
    setEditandoMozo(u.id);
    setEditBarra(u.id_barra_asignada ? String(u.id_barra_asignada) : '');
    setEditCaja(u.id_caja_asignada ? String(u.id_caja_asignada) : '');
    setEditPiso(u.piso_asignado !== null && u.piso_asignado !== undefined ? String(u.piso_asignado) : '');
  };

  const handleGuardarAsignacion = async (id) => {
    setGuardandoEdit(prev => ({ ...prev, [id]: true }));
    try {
      const body = {
        id_barra: editBarra ? parseInt(editBarra) : null,
        id_caja:  editCaja  ? parseInt(editCaja)  : null,
        piso_asignado: editPiso !== '' ? parseInt(editPiso) : -1,
      };
      const res = await fetch(getApiUrl(`/usuarios-admin/${id}`), {
        method: 'PUT', headers, body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Error al guardar');
      const barraObj = barras.find(b => b.id === data.id_barra_asignada);
      const cajaObj  = cajasAbiertas.find(c => c.id === data.id_caja_asignada);
      setUsuarios(prev => prev.map(u => u.id === id ? {
        ...u,
        id_barra_asignada: data.id_barra_asignada,
        nombre_barra:      barraObj?.nombre || null,
        id_caja_asignada:  data.id_caja_asignada,
        nombre_caja:       cajaObj?.nombre  || null,
        piso_asignado:     data.piso_asignado,
      } : u));
      setEditandoMozo(null);
      mostrarExito('Asignaciones del MOZO actualizadas.');
    } catch (e) { mostrarError(e.message); }
    finally { setGuardandoEdit(prev => ({ ...prev, [id]: false })); }
  };

  const formDisabled = creando || !nuevoNombre.trim() || !nuevoUsuario.trim() || !nuevoPassword.trim();

  const textoAsignacion = (u) => {
    if (u.rol === 'MOZO') {
      const partes = [];
      if (u.nombre_barra)  partes.push(`🍴 ${u.nombre_barra}`);
      if (u.nombre_caja)   partes.push(`🏧 ${u.nombre_caja}`);
      if (u.piso_asignado !== null && u.piso_asignado !== undefined)
        partes.push(`🏢 Piso ${u.piso_asignado === 0 ? '0 (Directos)' : u.piso_asignado}`);
      return partes.length > 0 ? partes.join(' · ') : '—';
    }
    return u.nombre_barra || '—';
  };

  return (
    <>
      <div>
        {/* Formulario nuevo usuario */}
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '1.25rem 1.5rem', marginBottom: '1.5rem', boxShadow: '0 4px 15px rgba(0,0,0,0.07)' }}>
          <h3 style={{ color: '#1e293b', fontSize: '1rem', fontWeight: 700, marginBottom: '1rem', marginTop: 0, borderLeft: '3px solid #f05b41', paddingLeft: '0.75rem' }}>
            Crear nuevo usuario
          </h3>
          <form onSubmit={handleCrear}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '0.6rem', marginBottom: '1rem' }}>
              <input type="text" placeholder="Nombre *" value={nuevoNombre} onChange={e => setNuevoNombre(e.target.value)} required style={inputStyle} />
              <input type="text" placeholder="Apellido" value={nuevoApellido} onChange={e => setNuevoApellido(e.target.value)} style={inputStyle} />
              <input type="text" placeholder="Usuario *" value={nuevoUsuario} onChange={e => setNuevoUsuario(e.target.value)} required style={inputStyle} />
              <input type="password" placeholder="Contraseña *" value={nuevoPassword} onChange={e => setNuevoPassword(e.target.value)} required style={inputStyle} />
              <select value={nuevoRol} onChange={e => { setNuevoRol(e.target.value); setNuevoIdBarra(''); setNuevoIdCaja(''); setNuevoPiso(''); }} style={inputStyle}>
                {ROLES_VALIDOS.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
              {['COCINERO', 'BARTENDER', 'MOZO'].includes(nuevoRol) && (
                <select value={nuevoIdBarra} onChange={e => setNuevoIdBarra(e.target.value)} style={inputStyle}>
                  <option value="">— Sin barra asignada —</option>
                  {barras.map(b => <option key={b.id} value={b.id}>{b.nombre}</option>)}
                </select>
              )}
              {nuevoRol === 'MOZO' && (
                <>
                  <select value={nuevoIdCaja} onChange={e => setNuevoIdCaja(e.target.value)} style={inputStyle}>
                    <option value="">— Sin caja asignada —</option>
                    {cajasAbiertas.map(c => <option key={c.id} value={c.id}>{c.nombre || `Caja #${c.id}`}</option>)}
                  </select>
                  <select value={nuevoPiso} onChange={e => setNuevoPiso(e.target.value)} style={inputStyle}>
                    <option value="">— Sin restricción de piso —</option>
                    <option value="0">Piso 0 (Pedidos Directos)</option>
                    {pisosDisponibles.map(p => <option key={p} value={p}>Piso {p}</option>)}
                  </select>
                </>
              )}
            </div>
            <button
              type="submit"
              disabled={formDisabled}
              style={{
                padding: '0.5rem 1.5rem', borderRadius: 8, border: 'none',
                background: formDisabled ? '#e2e8f0' : 'linear-gradient(to right, #f05b41, #f78361)',
                color: formDisabled ? '#94a3b8' : '#fff',
                fontWeight: 600, cursor: formDisabled ? 'not-allowed' : 'pointer',
                fontSize: '0.9rem', transition: 'all 0.2s',
                boxShadow: formDisabled ? 'none' : '0 2px 8px rgba(240,91,65,0.25)',
              }}
            >
              {creando ? 'Creando...' : '+ Nuevo Usuario'}
            </button>
          </form>
        </div>

        <MsgError msg={error} />
        <MsgExito msg={exito} />

        {loading ? (
          <p style={{ color: '#64748b' }}>Cargando usuarios...</p>
        ) : usuarios.length === 0 ? (
          <p style={{ color: '#64748b' }}>No hay usuarios registrados.</p>
        ) : (
          <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, overflowX: 'auto', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
            <table style={{ minWidth: '850px', width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                  {['Nombre', 'Usuario', 'Rol', 'Asignaciones', 'Estado', 'Acciones'].map((h, i) => (
                    <th key={i} style={{
                      textAlign: i >= 4 ? 'center' : 'left',
                      padding: '0.6rem 1rem', color: '#64748b',
                      fontSize: '0.8rem', textTransform: 'uppercase', fontWeight: 600,
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {usuarios.map(u => {
                  const badge = ROL_BADGE[u.rol] || { bg: '#f1f5f9', color: '#64748b' };
                  const esMozo = u.rol === 'MOZO';
                  const editandoEste = editandoMozo === u.id;
                  return (
                    <React.Fragment key={u.id}>
                      <tr style={{ borderBottom: editandoEste ? 'none' : '1px solid #e2e8f0', opacity: u.estado ? 1 : 0.55 }}>
                        <td style={{ padding: '0.75rem 1rem', color: '#1e293b', fontWeight: 500 }}>
                          {u.nombre} {u.apellido}
                        </td>
                        <td style={{ padding: '0.75rem 1rem', color: '#94a3b8', fontSize: '0.88rem' }}>
                          @{u.usuario}
                        </td>
                        <td style={{ padding: '0.75rem 1rem', textAlign: 'center' }}>
                          <span style={{ fontSize: '0.75rem', padding: '0.25rem 0.7rem', borderRadius: 999, background: badge.bg, color: badge.color, fontWeight: 600 }}>
                            {u.rol}
                          </span>
                        </td>
                        <td style={{ padding: '0.75rem 1rem', color: '#64748b', fontSize: '0.82rem' }}>
                          {textoAsignacion(u)}
                        </td>
                        <td style={{ padding: '0.75rem 1rem', textAlign: 'center' }}>
                          <span style={{
                            fontSize: '0.75rem', padding: '0.25rem 0.6rem', borderRadius: 999,
                            background: u.estado ? '#dcfce7' : '#f1f5f9',
                            color: u.estado ? '#15803d' : '#94a3b8', fontWeight: 600,
                          }}>
                            {u.estado ? 'Activo' : 'Inactivo'}
                          </span>
                        </td>
                        <td style={{ padding: '0.75rem 1rem', textAlign: 'center' }}>
                          <div style={{ display: 'flex', gap: '0.4rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                            <button
                              onClick={() => handleToggleEstado(u.id, u.estado)}
                              disabled={!!toggling[u.id]}
                              style={{
                                padding: '0.3rem 0.75rem', borderRadius: 6,
                                border: u.estado ? '1px solid #fca5a5' : '1px solid #86efac',
                                background: u.estado ? '#fee2e2' : '#dcfce7',
                                color: u.estado ? '#dc2626' : '#16a34a',
                                cursor: toggling[u.id] ? 'wait' : 'pointer',
                                fontSize: '0.8rem', fontWeight: 600,
                              }}
                            >
                              {toggling[u.id] ? '...' : u.estado ? 'Desactivar' : 'Activar'}
                            </button>
                            <button
                              onClick={() => abrirEdicionUsuario(u)}
                              style={{
                                padding: '0.3rem 0.75rem', borderRadius: 6,
                                border: '1px solid #c4b5fd', background: '#f5f3ff', color: '#6d28d9',
                                cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600,
                              }}
                            >
                              ✏️ Editar
                            </button>
                            {!u.estado && (
                              <button
                                onClick={() => confirmarEliminar(u)}
                                disabled={!!eliminando[u.id]}
                                title="Eliminar permanentemente este usuario"
                                style={{
                                  padding: '0.3rem 0.75rem', borderRadius: 6,
                                  border: '1px solid #fca5a5', background: '#fee2e2', color: '#dc2626',
                                  cursor: eliminando[u.id] ? 'wait' : 'pointer', fontSize: '0.8rem', fontWeight: 600,
                                }}
                              >
                                {eliminando[u.id] ? '...' : '🗑️'}
                              </button>
                            )}
                            {esMozo && (
                              <button
                                onClick={() => editandoEste ? setEditandoMozo(null) : abrirEdicionMozo(u)}
                                style={{
                                  padding: '0.3rem 0.75rem', borderRadius: 6,
                                  border: '1px solid #93c5fd', background: editandoEste ? '#dbeafe' : '#eff6ff',
                                  color: '#1d4ed8', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600,
                                }}
                              >
                                {editandoEste ? 'Cerrar' : '🔗 Asignar'}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                      {editandoEste && (
                        <tr style={{ borderBottom: '1px solid #e2e8f0', background: '#f8fafc' }}>
                          <td colSpan={6} style={{ padding: '0.75rem 1rem' }}>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center' }}>
                              <span style={{ color: '#475569', fontSize: '0.82rem', fontWeight: 600, marginRight: '0.25rem' }}>Asignaciones:</span>
                              <select value={editBarra} onChange={e => setEditBarra(e.target.value)} style={{ ...inputStyle, width: 'auto', minWidth: 160 }}>
                                <option value="">— Sin barra —</option>
                                {barras.map(b => <option key={b.id} value={b.id}>{b.nombre}</option>)}
                              </select>
                              <select value={editCaja} onChange={e => setEditCaja(e.target.value)} style={{ ...inputStyle, width: 'auto', minWidth: 160 }}>
                                <option value="">— Sin caja —</option>
                                {cajasAbiertas.map(c => <option key={c.id} value={c.id}>{c.nombre || `Caja #${c.id}`}</option>)}
                              </select>
                              <select value={editPiso} onChange={e => setEditPiso(e.target.value)} style={{ ...inputStyle, width: 'auto', minWidth: 160 }}>
                                <option value="">— Sin restricción de piso —</option>
                                <option value="0">Piso 0 (Pedidos Directos)</option>
                                {pisosDisponibles.map(p => <option key={p} value={p}>Piso {p}</option>)}
                              </select>
                              <button
                                onClick={() => handleGuardarAsignacion(u.id)}
                                disabled={!!guardandoEdit[u.id]}
                                style={{
                                  padding: '0.35rem 1rem', borderRadius: 6, border: 'none',
                                  background: guardandoEdit[u.id] ? '#e2e8f0' : '#f05b41',
                                  color: guardandoEdit[u.id] ? '#94a3b8' : '#fff',
                                  fontWeight: 600, cursor: guardandoEdit[u.id] ? 'wait' : 'pointer',
                                  fontSize: '0.85rem',
                                }}
                              >
                                {guardandoEdit[u.id] ? 'Guardando...' : 'Guardar'}
                              </button>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Modal Edición de Usuario ── */}
      {modalEdicion && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: '1.75rem 2rem', width: '100%', maxWidth: 440, boxShadow: '0 20px 60px rgba(0,0,0,0.18)' }}>
            <h3 style={{ margin: '0 0 1.25rem', fontSize: '1rem', fontWeight: 700, color: '#1e293b', borderLeft: '3px solid #f05b41', paddingLeft: '0.75rem' }}>
              Editar usuario: {modalEdicion.nombre}
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', marginBottom: '1.25rem' }}>
              <input type="text" placeholder="Nombre *" value={editNombre} onChange={e => setEditNombre(e.target.value)} style={inputStyle} />
              <input type="text" placeholder="Apellido" value={editApellido} onChange={e => setEditApellido(e.target.value)} style={inputStyle} />
              <input type="text" placeholder="Usuario (login) *" value={editUsuario} onChange={e => setEditUsuario(e.target.value)} style={inputStyle} />
              <input type="password" placeholder="Nueva contraseña (vacío = sin cambio)" value={editPassword} onChange={e => setEditPassword(e.target.value)} style={inputStyle} />
              <p style={{ margin: 0, fontSize: '0.78rem', color: '#94a3b8' }}>El rol y las asignaciones se gestionan desde los controles de la tabla.</p>
            </div>
            <div style={{ display: 'flex', gap: '0.6rem', justifyContent: 'flex-end' }}>
              <button onClick={() => setModalEdicion(null)} style={{ padding: '0.45rem 1.1rem', borderRadius: 6, border: '1px solid #e2e8f0', background: '#fff', color: '#64748b', fontWeight: 600, cursor: 'pointer', fontSize: '0.9rem' }}>Cancelar</button>
              <button onClick={handleEditarUsuario} disabled={guardandoEdicionU || !editNombre.trim() || !editUsuario.trim()} style={{ padding: '0.45rem 1.3rem', borderRadius: 6, border: 'none', background: (guardandoEdicionU || !editNombre.trim() || !editUsuario.trim()) ? '#e2e8f0' : 'linear-gradient(to right, #f05b41, #f78361)', color: (guardandoEdicionU || !editNombre.trim() || !editUsuario.trim()) ? '#94a3b8' : '#fff', fontWeight: 600, cursor: guardandoEdicionU ? 'wait' : 'pointer', fontSize: '0.9rem' }}>{guardandoEdicionU ? 'Guardando...' : 'Guardar cambios'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Diálogo Confirmar Eliminación ── */}
      {usuarioAEliminar && (
        <ConfirmDialog
          title="Eliminar usuario"
          message={`⚠️ ¿Eliminar PERMANENTEMENTE al usuario "${usuarioAEliminar.nombre} ${usuarioAEliminar.apellido || ''}" (@${usuarioAEliminar.usuario})? Esta acción NO se puede deshacer.`}
          confirmText="Eliminar"
          loading={!!eliminando[usuarioAEliminar.id]}
          onConfirm={handleEliminarUsuario}
          onCancel={() => setUsuarioAEliminar(null)}
        />
      )}
    </>
  );
};

// ─── Componente principal ──────────────────────────────────────────────────────
const UsuariosAdminComponent = () => {
  const [tab, setTab] = useState('barras');

  const TABS = [
    { key: 'barras',   label: 'Barras / Cocinas' },
    { key: 'cajeros',  label: 'Cajeros' },
    { key: 'usuarios', label: 'Gestión de Usuarios' },
  ];

  return (
    <div style={{ padding: '1.5rem 2rem', maxWidth: '1200px', margin: '0 auto' }}>
      <h1 style={{ fontSize: '1.6rem', fontWeight: 700, color: '#1e293b', marginBottom: '1.75rem', paddingBottom: '1rem', borderBottom: '3px solid #f05b41' }}>
        Gestión de Usuarios
      </h1>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '0', marginBottom: '1.5rem', borderBottom: '2px solid #e2e8f0' }}>
        {TABS.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            style={{
              padding: '0.6rem 1.4rem',
              border: 'none',
              borderBottom: tab === t.key ? '3px solid #f05b41' : '3px solid transparent',
              background: 'transparent',
              color: tab === t.key ? '#f05b41' : '#64748b',
              fontWeight: tab === t.key ? 700 : 500,
              cursor: 'pointer',
              fontSize: '0.95rem',
              marginBottom: '-2px',
              transition: 'color 0.15s',
              fontFamily: 'inherit',
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'barras'   && <BarrasAdminComponent />}
      {tab === 'cajeros'  && <TabCajeros />}
      {tab === 'usuarios' && <TabGestionUsuarios />}
    </div>
  );
};

export default UsuariosAdminComponent;
